import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import LoginPage from './features/auth/LoginPage';
import WelcomePage from './features/auth/WelcomePage';
import { UIProvider } from './shared/components/ui';
import { AdminStateProvider } from './hooks/useAdminState';
import { readApp, readProgramId, type AppName } from './shared/utils/appUrl';
import { shouldBlockFrame } from './shared/utils/embedGuard';

// Route-level code-splitting: each portal below is its own downloaded chunk, so e.g. an Ops Hub
// volunteer or a public Household/Donor visitor never fetches the Admin Portal's JS.
const HouseholdPortal = React.lazy(() => import('./features/household/HouseholdPortal'));
const OperationsHub = React.lazy(() => import('./features/operations/OperationsHub'));
const VolunteerGateway = React.lazy(() => import('./features/operations/VolunteerGateway'));
const AdminDashboard = React.lazy(() => import('./features/admin/AdminDashboard'));
const DonorPortal = React.lazy(() => import('./features/donor/DonorPortal'));
// RegistryModalProvider pulls in the entire admin/forms/ subdirectory + ReceivingDesk (~3,500
// lines) — must be split alongside the dashboards above, or that Admin-only code still ships to
// every visitor regardless of portal.
const RegistryModalProvider = React.lazy(() =>
  import('./features/admin/RegistryModalProvider').then((m) => ({ default: m.RegistryModalProvider }))
);

const CHUNK_RELOAD_FLAG = 'grm_chunk_reload_attempted';

function PortalLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-brand-command animate-spin" />
    </div>
  );
}

/**
 * Guards the lazy-loaded portal chunks: a stale tab open across a redeploy can fail a lazy
 * chunk fetch mid-session (the old build hash no longer exists on the server). First occurrence
 * auto-reloads once; if that doesn't fix it (or a genuine render error occurred), falls back to
 * a manual-reload message instead of looping forever.
 */
class PortalErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    const isChunkLoadError = /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i.test(
      message
    );
    if (isChunkLoadError && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      if (!sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
        // componentDidCatch (which sets the flag and reloads) runs after this render commits.
        return <PortalLoadingFallback />;
      }
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4">
          <p className="text-primary">Something went wrong loading this page.</p>
          <button
            type="button"
            className="text-brand-command underline"
            onClick={() => {
              sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
              window.location.reload();
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Shown instead of a non-public view when it is loaded inside another site's frame (clickjacking guard, DL-523). */
function FramedNotice() {
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 text-center px-4">
      <p className="text-primary text-brand-command">This page can't be shown inside another site.</p>
      <a className="text-brand-command underline" href={window.location.href} target="_blank" rel="noopener noreferrer">
        Open it in a new tab
      </a>
    </div>
  );
}

/**
 * PortalSwitch: The Master Routing Hub
 * 
 * Implements the 8 Canonical Entry Points:
 * 1. household: Public enrollment for beneficiaries.
 * 2. appointments: Access-code login for beneficiary self-service.
 * 3. volunteer: Identification, registration, and shift browsing.
 * 4. reimbursement: Access-code login for volunteer receipt uploads.
 * 5. ops: Google or access-code login for active shift Site/logistics tools.
 * 6. admin: Google SSO access for program management.
 * 7. tree: Public virtual tag tree for donors.
 * 8. tag: Public/QR access for donor tag reporting.
 */
function PortalSwitch() {
  const [searchParams] = useSearchParams();

  // One URL scheme for every entry point (ADR-128, DL-520): `?a={app code}&p={programId}`. No path entry points and
  // no long-form `app` / `db` / `dbId` aliases. Everything below works in terms of the long app names.
  const appType: AppName | null = readApp(searchParams);
  const dbId = readProgramId(searchParams);

  // `provision` runs before any Program exists; every other app needs a Program id.
  const dbRestrictedApps: AppName[] = ['admin', 'ops', 'household', 'apply', 'tree', 'tag'];

  useEffect(() => {
    if (dbId) {
      window.GRM_CONFIG = { ...window.GRM_CONFIG, dbId };
    }
  }, [dbId]);

  // Global Theme Sync: Apply portal class to document.body
  useEffect(() => {
    const classList = document.body.classList;
    // Remove existing portal classes
    classList.remove('app-admin', 'app-public', 'app-ops');
    
    // Apply current context
    if (appType === 'admin') classList.add('app-admin');
    else if (appType === 'ops') classList.add('app-ops');
    else classList.add('app-public');

    return () => classList.remove('app-admin', 'app-public', 'app-ops');
  }, [appType]);
  
  // Requirement: Any call with missing (null) or bad parameters triggers the bad url screen.
  // Provisioning must be intentional via ?a=p.
  if (!appType) {
    return <WelcomePage mode="invalid-app" />;
  }

  if (shouldBlockFrame(appType)) {
    return <FramedNotice />;
  }

  const hasDb = !!dbId || !!(window.GRM_CONFIG && window.GRM_CONFIG.dbId);
  
  if (dbRestrictedApps.includes(appType) && !hasDb) {
    return <WelcomePage mode="invalid-app" />;
  }

  const hasOpsToken = !!localStorage.getItem('volunteer_session_token') || !!sessionStorage.getItem('volunteer_session_token');

  return (
    <div className="min-h-dvh">
      <PortalErrorBoundary>
      <Suspense fallback={<PortalLoadingFallback />}>
      {(() => {
        switch (appType) {
          case 'admin':
            return (
              <AdminStateProvider>
                <RegistryModalProvider>
                  <AdminDashboard />
                </RegistryModalProvider>
              </AdminStateProvider>
            );
          
          case 'ops':
            // ADR-129: the one Ops Hub entry — VolunteerGateway offers Google or an emailed code to everyone.
            // Ops sessions live under their own token key, separate from the Admin Portal's.
            return (
              <AdminStateProvider opsMode>
                {hasOpsToken ? (
                  <RegistryModalProvider>
                    <OperationsHub />
                  </RegistryModalProvider>
                ) : (
                  <VolunteerGateway />
                )}
              </AdminStateProvider>
            );
          
          case 'apply':
          case 'household':
            return <HouseholdPortal />;
          
          case 'tree':
          case 'tag':
            return <DonorPortal />;
          
          case 'provision':
            return <WelcomePage mode="unconfigured" />;

          default:
            return <WelcomePage mode="invalid-app" />;
        }
      })()}
      </Suspense>
      </PortalErrorBoundary>
    </div>
  );
}

function App() {
  useEffect(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  }, []);

  return (
    <Router>
      <UIProvider>
        <Routes>
          <Route path="/auth/login" element={shouldBlockFrame(null) ? <FramedNotice /> : <LoginPage />} />
          <Route path="*" element={<PortalSwitch />} />
        </Routes>
      </UIProvider>
    </Router>
  );
}

export default App;
